import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@contexts/AuthContext';
import {
  Search,
  Edit,
  RefreshCw,
  AlertTriangle,
  Eye,
  CheckCircle,
  XCircle,
  Shield,
  Users as UsersIcon,
  UserCheck,
  BarChart3,
  Trash2,
  LogOut,
  X,
  Link2
} from 'lucide-react';
import { usersApi } from '@domains/admin/api/users.api';
import { companiesApi, type Company } from '../../../domains/companies/api';
import { partnerApi, type Partner } from '../../partner/api/partner.api';
import { impersonationApi } from '@domains/admin/api/impersonation.api';
import { notify } from '@core/notifications/notify';
import { useModal, AdminUserModal, ConfirmModal } from '@components/modal';
import { socketService } from '../../../services/socket.service';
import type { UserWithRoles } from '@domains/admin/api/users.api';
import PageInfoButton from '@/components/PageInfoButton';

// Protected system users that cannot be modified by admins
const PROTECTED_USER_EMAILS = [
  'support@eis-tech.co.uk',
  'support@yiontech.co.uk'
];

const isUserProtected = (userEmail: string): boolean => {
  return PROTECTED_USER_EMAILS.includes(userEmail.toLowerCase());
};

const Users: React.FC = () => {
  const { hasPermission, user: currentUser, isLoading: authLoading } = useAuth();
  const { openModal, closeModal } = useModal();
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [availablePermissions, setAvailablePermissions] = useState<string[]>([]);
  const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
  const [availablePartners, setAvailablePartners] = useState<{ id: number; name: string }[]>([]);
  const [permissionProfiles, setPermissionProfiles] = useState<{[key: number]: string}>({});

  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [showAnalytics, setShowAnalytics] = useState(false);


  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const loadPermissionProfiles = async () => {
    try {
      const profiles = await usersApi.getUserTypes(true);
      const profileMap: {[key: number]: string} = {};
      profiles.forEach(profile => {
        profileMap[profile.id] = profile.name;
      });
      setPermissionProfiles(profileMap);
    } catch (error) {
      console.error('Error loading permission profiles:', error);
      setPermissionProfiles({});
    }
  };

  useEffect(() => {
    loadUsers();
    loadAvailablePermissions();
    loadAvailableCompanies();
    loadPermissionProfiles();
    loadAvailablePartners();

    // Restore scroll position
    const savedScrollPosition = sessionStorage.getItem('users-page-scroll-position');
    if (savedScrollPosition) {
      setTimeout(() => {
        const mainElement = document.querySelector('main');
        if (mainElement) {
          mainElement.scrollTop = parseInt(savedScrollPosition, 10);
        }
      }, 100);
    }

    // Connect to WebSocket for real-time updates
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.id) {
      socketService.connect(user.id);
    }

    // Listen for user status changes
    const handleUserStatusChange = (event: CustomEvent) => {
      const { userId, status, lastLoginAt, lastLoginIp } = event.detail;
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId
            ? {
                ...user,
                is_online: status === 'online',
                last_login_at: lastLoginAt || user.last_login_at,
                last_login_ip: lastLoginIp || user.last_login_ip
              }
            : user
        )
      );
    };

    window.addEventListener('userStatusChanged', handleUserStatusChange as EventListener);

    // Save scroll position on scroll
    const handleScroll = () => {
      const mainElement = document.querySelector('main');
      if (mainElement) {
        sessionStorage.setItem('users-page-scroll-position', mainElement.scrollTop.toString());
      }
    };

    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('userStatusChanged', handleUserStatusChange as EventListener);
      if (mainElement) {
        mainElement.removeEventListener('scroll', handleScroll);
      }
      // Don't disconnect socket - let the socket service manage the connection
      // This prevents race conditions when multiple components use the socket
    };
  }, []);

  const loadUsers = async (page = 1) => {
    setIsLoading(true);
    try {
      // Company admins only see their own company's users
      const filters = currentUser?.company_id ? { company_id: currentUser.company_id } : undefined;
      const result = await usersApi.getUsers(1, 500, searchTerm, filters);
      const allUsers = result.users;
      const total = allUsers.length;
      const limit = 20;
      const pages = Math.ceil(total / limit);
      const start = (page - 1) * limit;
      setUsers(allUsers.slice(start, start + limit));
      setPagination({ page, limit, total, pages });
    } catch (error) {
      console.error('Error loading users:', error);
      notify.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailablePermissions = async () => {
    try {
      const permissions = await usersApi.getAssignablePermissions();
      setAvailablePermissions(permissions);
    } catch (error) {
      console.error('Error loading permissions:', error);
      // Fallback to basic permissions if API fails
      const fallbackPermissions = [
        'users.read', 'users.create', 'users.update', 'users.delete',
        'companies.read', 'companies.create', 'companies.update', 'companies.delete',
        'frameworks.read', 'frameworks.create', 'frameworks.update', 'frameworks.delete',
        'compliance.read', 'compliance.create', 'compliance.update', 'compliance.delete',
        'reports.read', 'reports.create', 'reports.export',
        'audit_logs.view', 'system_logs.view'
      ];
      setAvailablePermissions(fallbackPermissions);
    }
  };

  const loadAvailableCompanies = async () => {
    try {
      const companies = await companiesApi.getCompanies();
      setAvailableCompanies(companies);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const loadAvailablePartners = async () => {
    try {
      const res = await partnerApi.listPartners();
      setAvailablePartners(res.partners.map((p: Partner) => ({ id: p.id, name: p.name })));
    } catch (error) {
      console.error('Error loading partners:', error);
    }
  };

  const handleSaveUser = async (userData: any) => {
    try {
      if (userData.id !== null) {
        await usersApi.updateUser(userData.id, {
          first_name: userData.first_name,
          last_name: userData.last_name,
          company_id: userData.company_id || undefined,
          partner_id: userData.partner_id || undefined,
          is_active: userData.is_active,
          user_types: userData.user_types || [],
          permissionKeys: userData.permissions || []
        });
      } else {
        await usersApi.createUser({
          email: userData.email,
          first_name: userData.first_name,
          last_name: userData.last_name,
          company_id: userData.company_id || undefined,
          partner_id: userData.partner_id || undefined,
          user_types: userData.user_types || [],
          permissionKeys: userData.permissions || []
        });
      }
      notify.success('User saved successfully!');
      loadUsers(pagination.page);
    } catch (error) {
      console.error('Error saving user:', error);
      notify.error('Failed to save user');
    }
  };

  const handleDeactivateUser = (user: UserWithRoles) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Deactivate User',
        message: `Are you sure you want to deactivate ${user.first_name} ${user.last_name}? The user will lose access but all data will be preserved.`,
        confirmText: 'Deactivate User',
        cancelText: 'Cancel',
        type: 'warning',
        onConfirm: async () => {
          try {
            await usersApi.deactivateUser(user.id, 'Administrative deactivation');
            notify.success('User deactivated successfully!');
            loadUsers(pagination.page);
          } catch (error) {
            console.error('Error deactivating user:', error);
            notify.error('Failed to deactivate user');
          }
        },
        onCancel: () => {},
      },
    });
  };

  const handleReactivateUser = (user: UserWithRoles) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Reactivate User',
        message: `Are you sure you want to reactivate ${user.first_name} ${user.last_name}? The user will regain access.`,
        confirmText: 'Reactivate User',
        cancelText: 'Cancel',
        type: 'success',
        onConfirm: async () => {
          try {
            await usersApi.reactivateUser(user.id);
            notify.success('User reactivated successfully!');
            loadUsers(pagination.page);
          } catch (error) {
            console.error('Error reactivating user:', error);
            notify.error('Failed to reactivate user');
          }
        },
        onCancel: () => {},
      },
    });
  };

  const handleKillUserSessions = (user: UserWithRoles) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Kill User Sessions',
        message: `Are you sure you want to terminate all active sessions for ${user.first_name} ${user.last_name}? This will force the user to log out from all devices.`,
        confirmText: 'Kill Sessions',
        cancelText: 'Cancel',
        type: 'warning',
        onConfirm: async () => {
          try {
            const result = await usersApi.killUserSessions(user.id);
            notify.success(`Successfully terminated ${result.data.killed} session(s)!`);
            loadUsers(pagination.page);
          } catch (error) {
            console.error('Error killing user sessions:', error);
            notify.error('Failed to kill user sessions');
          }
        },
        onCancel: () => {},
      },
    });
  };

  const handleDeleteUser = (user: UserWithRoles) => {
    const isCompanyAdmin = user.role?.toLowerCase().includes('admin') || false;
    const warningNote = isCompanyAdmin
      ? ' If this is the only company admin, the company will have no admin until a new one is assigned.'
      : '';

    openModal({
      component: ConfirmModal,
      props: {
        title: 'Delete User',
        message: `Are you sure you want to permanently delete ${user.first_name} ${user.last_name}? Their company records will be preserved and reassigned — the company itself will not be affected.${warningNote}`,
        confirmText: 'Delete User',
        cancelText: 'Cancel',
        type: 'danger',
        onConfirm: async () => {
          try {
            const result = await usersApi.deleteUser(user.id);
            notify.success(result.details ? `User deleted. ${result.details}` : 'User deleted successfully!');
            loadUsers(pagination.page);
          } catch (error) {
            console.error('Error deleting user:', error);
            notify.error('Failed to delete user');
          }
        },
        onCancel: () => {},
      },
    });
  };

  const handleSuspendUser = (user: UserWithRoles) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Suspend User',
        message: `Are you sure you want to suspend ${user.first_name} ${user.last_name}? The user will be temporarily blocked.`,
        confirmText: 'Suspend User',
        cancelText: 'Cancel',
        type: 'warning',
        onConfirm: async () => {
          try {
            await usersApi.suspendUser(user.id, 'Administrative suspension');
            notify.success('User suspended successfully!');
            loadUsers(pagination.page);
          } catch (error) {
            console.error('Error suspending user:', error);
            notify.error('Failed to suspend user');
          }
        },
        onCancel: () => {},
      },
    });
  };

  const handleResetCredentials = (user: UserWithRoles) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Reset User Password',
        message: `Are you sure you want to reset ${user.first_name} ${user.last_name}'s password? They will receive an email with a secure reset link.`,
        confirmText: 'Send Reset Email',
        cancelText: 'Cancel',
        type: 'warning',
        onConfirm: async () => {
          try {
            await usersApi.resetUserCredentials(user.id);
            notify.success('Password reset email sent successfully!');
            loadUsers(pagination.page);
          } catch (error) {
            console.error('Error resetting credentials:', error);
            notify.error('Failed to send password reset email');
          }
        },
        onCancel: () => {},
      },
    });
  };



  const handleImpersonateUser = async (user: UserWithRoles) => {
    // Check if user has impersonation permission
    if (!hasPermission('users.impersonate')) {
      notify.error('You do not have permission to impersonate users');
      return;
    }

    try {
      const response = await impersonationApi.startImpersonation({
        targetUserId: user.id
      });

      if (response.success) {
        // Open new window/tab with impersonation token
        const impersonationUrl = `${window.location.origin}?impersonation_token=${response.data.token}`;
        window.open(impersonationUrl, '_blank');

        notify.success(`Impersonation session started for ${user.first_name} ${user.last_name}`);
      }
    } catch (error) {
      console.error('Error starting impersonation:', error);
      notify.error('Failed to start impersonation session');
    }
  };

  const handleViewSessions = async (user: UserWithRoles) => {
    try {
      const response = await usersApi.getUserSessions(user.id);
      if (response.success) {
        // Open modal to show sessions
        openModal({
          component: ({ sessions, userName }: { sessions: any[], userName: string }) => (
            <div className="p-6 max-w-2xl">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Active Sessions - {userName}</h2>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {sessions.length === 0 ? (
                  <div className="text-slate-500 py-8">
                    <p className="text-sm">No active sessions found</p>
                  </div>
                ) : (
                  sessions.map((session: any, index: number) => {
                    const isCurrentSession = index === 0; // Assume first session is current
                    const deviceName = session.user_agent?.includes('Mobile') ? 'Mobile Device' :
                                      session.user_agent?.includes('iOS') ? 'iOS Device' :
                                      session.user_agent?.includes('Android') ? 'Android Device' :
                                      'Desktop Device';

                    return (
                      <div key={session.id} className={`p-4 border border-slate-200 rounded-lg ${isCurrentSession ? 'bg-emerald-50/20' : 'bg-white'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`h-2 w-2 rounded-full ${isCurrentSession ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`}></div>
                            <div>
                              <p className={`text-sm font-bold ${isCurrentSession ? 'text-slate-900' : 'text-slate-700'}`}>
                                {deviceName} {isCurrentSession ? '(Current)' : ''}
                              </p>
                              <p className="text-xs font-mono text-slate-400">
                                IP: {session.ip_address || 'Unknown'} • {new Date(session.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-bold ${isCurrentSession ? 'text-slate-400' : 'text-slate-500'}`}>
                              {session.is_active ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ),
          props: {
            sessions: response.data,
            userName: `${user.first_name} ${user.last_name}`
          },
          options: { size: 'large' }
        });
      }
    } catch (error) {
      console.error('Error loading user sessions:', error);
      notify.error('Failed to load user sessions');
    }
  };

  // Helper function to determine user status based on is_active, is_online, and expires_at
  const getUserStatus = (user: UserWithRoles): { status: string; duration: string } => {
    if (!user.is_active) {
      return { status: 'Inactive', duration: 'Account disabled' };
    }

    // Check if user has active session (is_online from backend)
    if (user.is_online) {
      const sessionCount = user.active_session_count || 0;
      return { status: 'Online', duration: `${sessionCount} active session${sessionCount !== 1 ? 's' : ''}` };
    }

    if (user.expires_at) {
      const expiryDate = new Date(user.expires_at);
      const now = new Date();
      const daysUntilExpiry = Math.floor((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      if (expiryDate < now) {
        return { status: 'Offline', duration: `Expired ${Math.abs(daysUntilExpiry)}d ago` };
      } else if (daysUntilExpiry <= 7) {
        return { status: 'Away', duration: `Expires in ${daysUntilExpiry}d` };
      }
    }

    // Check last login time
    if (user.last_login_at) {
      const lastLogin = new Date(user.last_login_at);
      const now = new Date();
      const minutesSinceLogin = Math.floor((now.getTime() - lastLogin.getTime()) / (1000 * 60));
      const hoursSinceLogin = Math.floor(minutesSinceLogin / 60);

      if (minutesSinceLogin < 1) {
        return { status: 'Recently Active', duration: 'Just now' };
      } else if (minutesSinceLogin < 60) {
        return { status: 'Offline', duration: `Last seen ${minutesSinceLogin} minute${minutesSinceLogin !== 1 ? 's' : ''} ago` };
      } else if (hoursSinceLogin < 24) {
        return { status: 'Offline', duration: `Last seen ${hoursSinceLogin} hour${hoursSinceLogin !== 1 ? 's' : ''} ago` };
      } else if (hoursSinceLogin < 168) { // 7 days
        const days = Math.floor(hoursSinceLogin / 24);
        return { status: 'Offline', duration: `Last seen ${days} day${days !== 1 ? 's' : ''} ago` };
      }
    }

    return { status: 'Offline', duration: 'No recent activity' };
  };

  // Analytics calculations
  const analytics = useMemo(() => {
    const totalUsers = pagination.total || users.length;
    // For now, these counts are based on current page data
    // TODO: Implement server-side statistics API for accurate totals
    const activeUsers = users.filter(u => u.is_active).length;
    const inactiveUsers = users.filter(u => !u.is_active).length;
    const onlineUsers = users.filter(u => u.is_online).length;

    // Role distribution
    const roleCounts = users.reduce((acc, user) => {
      if (user.userTypes && user.userTypes.length > 0) {
        user.userTypes.forEach(profileId => {
          const roleName = permissionProfiles[profileId] || `Profile ${profileId}`;
          acc[roleName] = (acc[roleName] || 0) + 1;
        });
      } else {
        acc['No Profile'] = (acc['No Profile'] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    const topRoles = Object.entries(roleCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);

    // Recent registrations (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentUsers = users.filter(u =>
      new Date(u.created_at) > thirtyDaysAgo
    ).length;

    return {
      totalUsers,
      activeUsers,
      inactiveUsers,
      onlineUsers,
      topRoles,
      recentUsers,
      avgUsersPerMonth: Math.round(totalUsers / 12) // Rough estimate
    };
  }, [users, permissionProfiles]);



  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!hasPermission('users.read')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-white rounded-3xl border border-slate-100 p-8">
          <AlertTriangle className="text-red-500 mb-3" size={36} />
          <h1 className="text-lg font-bold text-slate-900 mb-1">Access Denied</h1>
          <p className="text-slate-500 text-sm">Only Super Admin or Platform Admin can access Users management.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-500 text-sm">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">

        {/* Page Heading */}
        <div className="mb-8 text-left">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900 text-left">Platform Users</h1><PageInfoButton /></div>
              <p className="text-slate-500 text-left">Manage {analytics.totalUsers} users across system admins, companies, auditors, and partners.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => loadUsers(pagination.page)}
                className="px-4 py-2.5 bg-white border border-slate-200 text-slate-600 font-medium text-xs rounded-full hover:bg-slate-50 transition-colors flex items-center gap-2"
              >
                <RefreshCw size={14} />
                Refresh
              </button>
              <button
                onClick={() => {
                  const modalId = openModal({
                    component: AdminUserModal,
                    props: {
                      onSubmit: handleSaveUser,
                      onClose: () => closeModal(modalId),
                      selectedUser: null,
                      availableCompanies,
                      availablePartners,
                      availablePermissions,
                      onRefreshProfiles: loadPermissionProfiles,
                    },
                    options: { size: 'large' }
                  });
                }}
                className="px-5 py-2.5 bg-blue-600 text-white font-bold text-sm rounded-full hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-lg shadow-blue-200"
              >
                <UsersIcon size={14} />
                Add New User
              </button>
            </div>
          </div>
        </div>

        {/* Stats — glass cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            <p className="text-xs text-slate-400 font-bold mb-1">TOTAL</p>
            <p className="text-3xl font-bold text-slate-900">{analytics.totalUsers}</p>
            <p className="text-[10px] text-slate-400">All platform users</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            <p className="text-xs text-slate-400 font-bold mb-1">ACTIVE</p>
            <p className="text-2xl font-bold text-emerald-600">{analytics.activeUsers}</p>
            <p className="text-[10px] text-slate-400">Currently enabled</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            <p className="text-xs text-slate-400 font-bold mb-1">ONLINE</p>
            <p className="text-2xl font-bold text-blue-600">{analytics.onlineUsers}</p>
            <p className="text-[10px] text-slate-400">Connected now</p>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            <p className="text-xs text-slate-400 font-bold mb-1">RECENT (30D)</p>
            <p className="text-3xl font-bold text-slate-900">{analytics.recentUsers}</p>
            <p className="text-[10px] text-blue-500 font-bold">{analytics.recentUsers > 0 ? `↑ ${analytics.recentUsers} new` : '—'}</p>
          </div>
        </div>

        {/* Analytics Dashboard */}
        {showAnalytics && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <h3 className="text-sm font-bold text-slate-900 mb-3">User Status</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Active Users</span>
                  <span className="text-sm font-bold text-emerald-600">{analytics.activeUsers}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Inactive Users</span>
                  <span className="text-sm font-bold text-red-600">{analytics.inactiveUsers}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Online Now</span>
                  <span className="text-sm font-bold text-blue-600">{analytics.onlineUsers}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <h3 className="text-sm font-bold text-slate-900 mb-3">Top Roles</h3>
              <div className="space-y-2">
                {analytics.topRoles.slice(0, 4).map(([role, count]: [string, number]) => (
                  <div key={role} className="flex items-center justify-between">
                    <span className="text-sm text-slate-700 capitalize">{role}</span>
                    <span className="text-sm font-bold text-slate-600">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <h3 className="text-sm font-bold text-slate-900 mb-3">Recent Activity</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">New Registrations</span>
                  <span className="text-sm font-bold text-slate-900">{analytics.recentUsers}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Avg per Month</span>
                  <span className="text-sm font-bold text-slate-900">{analytics.avgUsersPerMonth}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Total Users</span>
                  <span className="text-sm font-bold text-slate-900">{analytics.totalUsers}</span>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Table */}
      <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Platform Users</h3>
              <p className="text-xs text-slate-500">All platform-level accounts</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && loadUsers(1)}
                  className="pl-10 pr-8 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all w-56"
                />
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      loadUsers(1);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <button
                onClick={() => setShowAnalytics(!showAnalytics)}
                className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                  showAnalytics
                    ? 'bg-blue-50 text-blue-700'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <BarChart3 size={15} />
                Analytics
              </button>
              <span className="bg-slate-100 text-slate-700 px-3 py-2 rounded-lg text-xs font-bold">
                {pagination.total}
              </span>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="pl-5 py-3 w-12"><input type="checkbox" className="rounded border-slate-300" /></th>
                <th className="px-5 py-3 text-[10px] font-bold text-slate-400 uppercase">User Details</th>
                <th className="px-5 py-3 text-[10px] font-bold text-slate-400 uppercase">Company</th>
                <th className="px-5 py-3 text-[10px] font-bold text-slate-400 uppercase">Profile</th>
                <th className="px-5 py-3 text-[10px] font-bold text-slate-400 uppercase">Status</th>
                <th className="px-5 py-3 text-[10px] font-bold text-slate-400 uppercase">Last Login Time</th>
                <th className="px-5 py-3 text-[10px] font-bold text-slate-400 uppercase">Last Login IP</th>
                <th className="px-5 py-3 text-[10px] font-bold text-slate-400 uppercase">Created</th>
                <th className="px-5 py-3 text-left text-[10px] font-bold text-slate-400 uppercase pr-5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((user, _index) => {
                const { status, duration } = getUserStatus(user);
                const isOnline = status === 'Online';
                return (
                  <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                    <td className="pl-5 py-3 text-left"><input type="checkbox" className="rounded border-slate-300" /></td>
                    <td className="px-5 py-3 text-left">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center font-bold text-white text-xs uppercase">
                          {user.first_name?.[0]}{user.last_name?.[0]}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900">{user.first_name} {user.last_name}</div>
                          <div className="text-xs text-slate-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-left">
                      {user.userTypes?.includes(5) ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-violet-50 text-violet-700 border border-violet-200 rounded-full font-medium">
                          <Link2 size={10} />
                          Partner Reseller
                        </span>
                      ) : user.company_id ? (
                        <div>
                          <div className="text-sm font-medium text-slate-700">
                            {user.company_name || user.company?.name || availableCompanies.find(c => c.id === user.company_id)?.name || `Company #${user.company_id}`}
                          </div>
                          <div className="text-xs text-slate-400 font-mono">ID: {user.company_id}</div>
                        </div>
                      ) : (
                        <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 border border-slate-200 rounded-full font-medium">
                          Platform
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-left">
                      <div className="flex flex-col gap-1">
                        {user.userTypes && user.userTypes.length > 0 ? (
                          user.userTypes.map(profileId => (
                            <div key={profileId} className="mb-0.5">
                              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-blue-100 text-blue-700">
                                {permissionProfiles[profileId] || `Profile ${profileId}`}
                              </span>
                            </div>
                          ))
                        ) : (
                          <span className="text-xs text-slate-400 italic">No profile assigned</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-left">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-slate-300'} ${isOnline ? 'animate-pulse' : ''}`} style={{ animation: isOnline ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : '' }}></div>
                        <div>
                          <div className="text-sm font-medium text-slate-800 leading-none">{status}</div>
                          <div className="text-xs text-slate-500">{duration}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-left text-sm text-slate-500">
                      {user.last_login_at ? (
                        <div>
                          <div className="text-sm text-slate-700">
                            {new Date(user.last_login_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                          <div className="text-xs text-slate-500">
                            {new Date(user.last_login_at).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400 italic">Never</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-left">
                      {user.last_login_ip ? (
                        <span className="font-mono text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded border border-slate-200">
                          {user.last_login_ip}
                        </span>
                      ) : (
                        <span className="font-mono text-xs bg-slate-50 text-slate-400 px-2 py-1 rounded border border-slate-200">
                          No IP
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-left text-sm text-slate-500">
                      {new Date(user.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="px-5 py-3 text-left pr-5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleViewSessions(user)}
                          className="p-1.5 text-slate-400 hover:text-blue-600 transition-colors"
                          title="View Sessions"
                        >
                          <Shield size={15} />
                        </button>
                        {hasPermission('users.impersonate') && user.id !== currentUser?.id && !isUserProtected(user.email) && (
                          <button
                            onClick={() => handleImpersonateUser(user)}
                            className="p-1.5 text-slate-400 hover:text-amber-600 transition-colors"
                            title="Impersonate User"
                          >
                            <Eye size={15} />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            const modalId = openModal({
                              component: AdminUserModal,
                              props: {
                                onSubmit: handleSaveUser,
                                onClose: () => closeModal(modalId),
                                selectedUser: user,
                                availableCompanies,
                                availablePartners,
                                availablePermissions,
                                readOnly: isUserProtected(user.email) && user.id !== currentUser?.id,
                                onRefreshProfiles: loadPermissionProfiles,
                              },
                              options: { size: 'large' }
                            });
                          }}
                          className={`p-1.5 text-slate-400 transition-colors ${
                            isUserProtected(user.email) && user.id !== currentUser?.id
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:text-blue-600'
                          }`}
                          title={isUserProtected(user.email) && user.id !== currentUser?.id ? "Protected System User - Read Only" : "Edit User"}
                          disabled={isUserProtected(user.email) && user.id !== currentUser?.id}
                        >
                          <Edit size={15} />
                        </button>
                        {hasPermission('users.reset_credentials') && (!isUserProtected(user.email) || user.id === currentUser?.id) && (
                          <button
                            onClick={() => handleResetCredentials(user)}
                            className="p-1.5 text-slate-400 hover:text-orange-600 transition-colors"
                            title="Reset Credentials"
                          >
                            <RefreshCw size={15} />
                          </button>
                        )}
                        {user.is_active && (hasPermission('users.deactivate') || (currentUser?.company_id && user.company_id === currentUser.company_id)) && !isUserProtected(user.email) && (
                          <button
                            onClick={() => handleDeactivateUser(user)}
                            className="p-1.5 text-slate-400 hover:text-yellow-600 transition-colors"
                            title="Deactivate User"
                          >
                            <XCircle size={15} />
                          </button>
                        )}
                        {!user.is_active && (hasPermission('users.reactivate') || (currentUser?.company_id && user.company_id === currentUser.company_id)) && !isUserProtected(user.email) && (
                          <button
                            onClick={() => handleReactivateUser(user)}
                            className="p-1.5 text-slate-400 hover:text-green-600 transition-colors"
                            title="Reactivate User"
                          >
                            <CheckCircle size={15} />
                          </button>
                        )}
                        {hasPermission('users.suspend') && !isUserProtected(user.email) && (
                          <button
                            onClick={() => handleSuspendUser(user)}
                            className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                            title="Suspend User"
                          >
                            <AlertTriangle size={15} />
                          </button>
                        )}
                        {hasPermission('users.update') && (user.active_session_count || 0) > 0 && (!isUserProtected(user.email) || user.id === currentUser?.id) && (
                          <button
                            onClick={() => handleKillUserSessions(user)}
                            className="p-1.5 text-slate-400 hover:text-orange-600 transition-colors"
                            title={`Kill User Sessions (Force Logout)${(user.active_session_count || 0) > 0 ? ` - ${user.active_session_count} active session(s)` : ''}`}
                          >
                            <LogOut size={15} />
                          </button>
                        )}
                        {hasPermission('users.delete') && !isUserProtected(user.email) && (
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                            title="Delete User"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                        {isUserProtected(user.email) && (
                          <span className="px-2 py-0.5 bg-purple-50 text-purple-700 border border-purple-200 rounded text-[9px] font-bold flex items-center gap-1">
                            <Shield size={10} />
                            PROTECTED
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="bg-slate-50 border-t border-slate-200 px-5 py-3 flex items-center justify-between">
          <div className="text-xs text-slate-400 flex items-center gap-4">
            <span>Page {pagination.page} of {pagination.pages || 1}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => loadUsers(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="p-1.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7" strokeWidth="2"/></svg>
            </button>
            <button
              onClick={() => loadUsers(pagination.page + 1)}
              disabled={pagination.page >= (pagination.pages || 1)}
              className="p-1.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeWidth="2"/></svg>
            </button>
          </div>
        </div>
      </div>

      </div>
    </div>
  );
};

export default Users;
